/* eslint-disable react/prop-types */
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/exporting";
import "highcharts/modules/export-data";
import "highcharts/modules/offline-exporting";
import styled from "styled-components";
import { Tooltip } from "antd";
import { useTable, usePagination, Column, Row } from "react-table";
import { find, mapValues, merge } from "lodash";

import { ThemeVarNames, useTheme } from "@theme";
import { DownloadButton, Button, ViewMode } from "@components/common";
import {
  ChartSettings,
  RoseModule,
  RoseModuleCodeSettings,
  RoseTimeseries,
  RoseTimeseriesGroup,
} from "@types";
import {
  composeChartOptions,
  fixAxes,
  fixColors,
} from "@utils/helpers/display/timeseries";

import { Tree } from "./Tree";
import { DetailsButton } from "./DetailsButton";
import { ChangeUnitsButton } from "./ChangeUnitsButton";
import { ChartEditor } from "./ChartEditor";
import UserContainer from "@utils/state/userContainer";

export type TimeseriesGroupProps = {
  roseObject: RoseTimeseriesGroup;
  mod: RoseModule;
  addModule: (partialMod: Partial<RoseModule>) => void;
  onModuleChanged: (newMod: RoseModule) => void;
  chartSettings: ChartSettings;
  onChartSettingsChanged: (newChartSettings: ChartSettings) => void;
  view: ViewMode;
  isTableShown: boolean;
};

export function TimeseriesGroup(props: TimeseriesGroupProps): JSX.Element {
  const {
    roseObject,
    addModule,
    mod,
    onModuleChanged,
    chartSettings,
    onChartSettingsChanged,
    view,
    isTableShown = true,
  } = props;
  const [tree, setTree] = useState(null);
  const theme = useTheme();
  const chartSettingsRef = useRef(chartSettings);
  const { user } = UserContainer.useContainer();
  chartSettingsRef.current = chartSettings;
  const getChartSettings = () => chartSettingsRef.current;

  const [highchartOptions, setHighchartOptions] = useState<Highcharts.Options>(
    () =>
      composeChartOptions({
        roseObject,
        theme,
        chartSettings,
        getChartSettings,
        onChartSettingsChanged,
        rangeShown: isTableShown || view !== "view",
        modSettings: mod?.modulesettings,
        isViewMode: view === "view",
        user,
      }),
  );
  const updateHighchartOptions = (options: Partial<Highcharts.Options>) => {
    setHighchartOptions({ ...highchartOptions, ...options });
  };

  useEffect(() => {
    const newChartSettings = { ...highchartOptions };
    const chartMapKey: string = RoseModuleCodeSettings.composeId(
      roseObject.data.map(({ code }) => code),
    );

    highchartOptions?.series?.forEach(
      (_: Highcharts.SeriesOptionsType, i: number) => {
        if (mod && mod.modulesettings) {
          const chartInModSettings = mod.modulesettings?.charts?.[
            chartMapKey
          ] as ChartSettings;

          const yAxisLength: number = Array.isArray(highchartOptions?.yAxis)
            ? highchartOptions.yAxis.length
            : 0;

          // 3 places where axes positions could be stored
          const isChartAxisIdxValid: boolean =
            chartInModSettings?.datasets?.[i]?.yAxis < yAxisLength;
          const isModuleSettingsCodeIdxValid: boolean =
            mod.modulesettings?.units?.[_?.code] < yAxisLength;
          const isModuleSettingsIdxValid: boolean =
            mod.modulesettings?.units?.[i] < yAxisLength;

          // case when user made changes to axes positions (should be preserved)
          if (
            isChartAxisIdxValid ||
            isModuleSettingsCodeIdxValid ||
            isModuleSettingsIdxValid
          ) {
            newChartSettings.series[i as number].yAxis =
              chartInModSettings?.datasets?.[i]?.yAxis ||
              mod.modulesettings?.units?.[_?.code] ||
              mod.modulesettings?.units?.[i] ||
              0;
          } else {
            // case when a new code cell is being run
            newChartSettings.series[i as number].yAxis = 0;
          }
        }
      },
    );

    // Performance: Batch both fixes into a single state update
    const fixedSettings = fixAxes(fixColors(newChartSettings, theme), theme);
    setHighchartOptions(fixedSettings);
  }, []);

  const changeDatasetUnits = useCallback(
    (unitIndex: number, datasetCode: string) => {
      const newChartSettings = { ...highchartOptions };

      highchartOptions?.series?.forEach(
        (
          series: Highcharts.SeriesOptionsType & { code: string },
          i: number,
        ) => {
          if (series.code === datasetCode) {
            newChartSettings.series[i as number].yAxis = unitIndex;
          }
        },
      );

      // Performance: Batch both fixes into a single state update
      const fixedSettings = fixAxes(fixColors(newChartSettings, theme), theme);
      setHighchartOptions(fixedSettings);
      onModuleChanged(
        RoseModule.create(mod, {
          units: {
            ...mod?.modulesettings.units,
            [datasetCode]: unitIndex,
          },
        }),
      );
    },
    [highchartOptions, theme, mod, onModuleChanged],
  );

  const changeAllDatasetUnits = useCallback(
    (unitIndex: number) => {
      const newChartSettings = { ...highchartOptions };
      highchartOptions?.series?.forEach(
        (_: Highcharts.SeriesOptionsType, i: number) => {
          newChartSettings.series[i as number].yAxis = unitIndex;
        },
      );

      // Performance: Batch both fixes into a single state update
      const fixedSettings = fixAxes(fixColors(newChartSettings, theme), theme);
      setHighchartOptions(fixedSettings);
      onModuleChanged(
        RoseModule.create(mod, {
          units: mapValues(mod?.modulesettings?.units, () => unitIndex),
        }),
      );
    },
    [highchartOptions, theme, mod, onModuleChanged],
  );

  const handleChartFormLabels = useCallback(
    (newChartSettings: ChartSettings) => {
      const newDataset = Object.entries(newChartSettings.datasets).map(
        ([key, value]) => {
          const obj = find(
            highchartOptions.series,
            (obj: Highcharts.SeriesOptionsType & { code: string }) =>
              obj.code === key,
          );
          const mergedObj = merge(obj, value);
          mergedObj.showInLegend = !!mergedObj.name;
          return mergedObj;
        },
      );

      setHighchartOptions((prev) => ({ ...prev, series: newDataset }));
    },
    [highchartOptions.series],
  );

  const changeYAxisSide = useCallback(
    (axisIndex: number) => {
      const newChartSettings = { ...highchartOptions };
      newChartSettings.yAxis[axisIndex].opposite =
        !newChartSettings.yAxis[axisIndex].opposite;
      setHighchartOptions(fixColors(fixAxes(newChartSettings, theme), theme));
      onChartSettingsChanged({
        ...chartSettings,
        yAxis: newChartSettings.yAxis,
      });
    },
    [highchartOptions, theme, chartSettings, onChartSettingsChanged],
  );

  // Memoize width factor calculation
  const widthFactor = useMemo(() => {
    switch (mod?.modulesettings?.width) {
      case "66%":
        return 0.66;
      case "50%":
        return 0.5;
      case "33%":
        return 0.33;
      default:
        return 1;
    }
  }, [mod?.modulesettings?.width]);

  const handleOnChartCustomizationChange = useCallback(
    (newChartSettings: ChartSettings) => {
      onChartSettingsChanged(newChartSettings);
      handleChartFormLabels(newChartSettings);

      const titleLength = newChartSettings.title?.text?.length ?? 0;
      let titleFontSize = 28;
      let legendFontSize = 20;
      if (titleLength > 45 * widthFactor) {
        titleFontSize = 18;
        legendFontSize = 16;
      } else if (titleLength > 30 * widthFactor) {
        titleFontSize = 23;
        legendFontSize = 18;
      }

      const legendSettings = {
        ...highchartOptions?.legend,
        itemStyle: {
          ...highchartOptions?.legend?.itemStyle,
          fontSize: legendFontSize.toString(),
        },
      };
      const titleSettings = {
        ...highchartOptions.title,
        text: newChartSettings.title?.text,
        style: { fontSize: titleFontSize.toString() },
      };
      const sourceSettings = {
        ...highchartOptions.credits,
        text:
          newChartSettings.source?.length > 0
            ? `source: ${newChartSettings.source}`
            : "",
      };
      setHighchartOptions(
        fixColors(
          {
            ...highchartOptions,
            legend: { ...legendSettings },
            title: { ...titleSettings },
            credits: { ...sourceSettings },
            yAxis: newChartSettings.yAxis,
          },
          theme,
        ),
      );
    },
    [
      onChartSettingsChanged,
      handleChartFormLabels,
      highchartOptions,
      theme,
      widthFactor,
    ],
  );

  const toggleChartDetailsVisibility = useCallback(
    (isVisible: boolean) => {
      updateHighchartOptions({
        title: {
          ...highchartOptions.title,
        },
        rangeSelector: {
          ...highchartOptions.rangeSelector,
          inputEnabled: isVisible,
          enabled: isVisible,
          allButtonsEnabled: isVisible,
        },
        legend: {
          ...highchartOptions.legend,
        },
        navigator: {
          ...highchartOptions.navigator,
          enabled: isVisible,
        },
        exporting: {
          ...highchartOptions.exporting,
          enabled: isVisible,
        },
        chart: {
          zoomType: isVisible ? "x" : undefined,
        },
      });
    },
    [highchartOptions, updateHighchartOptions],
  );

  useEffect(() => {
    if (!highchartOptions) {
      return;
    }

    if (view === "edit") {
      toggleChartDetailsVisibility(true);
    } else {
      toggleChartDetailsVisibility(false);
    }
  }, [view]);

  const hasYAxis = highchartOptions?.yAxis?.length > 0;

  return highchartOptions ? (
    <>
      <ChartEditor
        onChange={handleOnChartCustomizationChange}
        settings={{
          ...chartSettings,
          yAxis: highchartOptions.yAxis as Highcharts.YAxisOptions[],
        }}
        roseObject={roseObject}
      />
      {hasYAxis ? (
        <MemoChart
          options={{ ...highchartOptions, xAxisBounds: chartSettings.xAxis }}
          style={{ width: "100%", height: "500px" }}
        />
      ) : null}

      {view === "view" && !isTableShown ? null : (
        <TableV2
          options={highchartOptions}
          tree={tree}
          setTree={setTree}
          changeDatasetUnits={changeDatasetUnits}
          changeAllDatasetUnits={changeAllDatasetUnits}
          changeYAxisSide={changeYAxisSide}
          onAddModule={addModule}
        />
      )}
      {tree && <Tree autoFocus treeData={tree} addModule={addModule} />}
    </>
  ) : null;
}

type MemoChartProps = {
  options: Highcharts.Options | any;
  style?: React.CSSProperties;
};

/* eslint-disable react/display-name */
export const MemoChart = React.memo(
  (props: MemoChartProps) => {
    const { options, style } = props;
    const chartRef = useRef<{
      chart: Highcharts.Chart;
      container: React.RefObject<HTMLDivElement>;
    }>(null);
    const resizeTimeoutRef = useRef<number | null>(null);
    const hasInitializedRef = useRef(false);

    const setExtremes = useCallback(
      (min: number | null, max: number | null) => {
        chartRef.current?.chart?.xAxis?.[0].setExtremes(min, max);
      },
      [],
    );

    useEffect(() => {
      if (hasInitializedRef.current) return;
      hasInitializedRef.current = true;

      const chart = chartRef.current?.chart;
      if (!chart) return;

      // Batch the initial update
      chart.update(
        {
          rangeSelector: {
            inputEnabled: true,
            selected: 5,
          },
        },
        false, // Don't redraw yet
      );

      // Use requestAnimationFrame for smoother initial render
      // This is more performant than setTimeout and syncs with browser repaint
      requestAnimationFrame(() => {
        const container = chartRef.current?.container.current;
        if (!container || !chartRef.current?.chart) return;

        // Use reflow instead of setSize for better performance
        chartRef.current.chart.reflow();
      });

      return () => {
        if (resizeTimeoutRef.current) {
          cancelAnimationFrame(resizeTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (options?.xAxisBounds?.min || options?.xAxisBounds?.max) {
        setExtremes(options.xAxisBounds.min, options.xAxisBounds.max);
      }
    }, [options?.xAxisBounds?.min, options?.xAxisBounds?.max, setExtremes]);

    return (
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
        containerProps={{
          style,
        }}
      />
    );
  },
  (prevProps, nextProps) => prevProps.options === nextProps.options,
);

export type MapTableProps = {
  options: any;
  tree: any;
  setTree: (tree: any) => void;
  changeDatasetUnits: (unitIndex: number, datasetCode: string) => void;
  changeAllDatasetUnits: (unitIndex: number) => void;
  changeYAxisSide: (axisIndex: number) => void;
  shown?: boolean;
};

const COLUMNS: Column<RoseTimeseries | any>[] = [
  { Header: "Rosecode", accessor: "code" },
  { Header: "Organization", accessor: "actor" },
  { Header: "Axis", accessor: "units" },
  { Header: "", accessor: "actions" },
];

export function TableV2({
  options,
  tree,
  setTree,
  changeDatasetUnits,
  changeAllDatasetUnits,
  changeYAxisSide,
  shown = true,
}: MapTableProps): JSX.Element {
  const changeDatasetUnitsRef = useRef(null);

  useEffect(() => {
    changeDatasetUnitsRef.current = changeDatasetUnits;
  }, [changeDatasetUnits]);

  const { columns, data } = useMemo(
    () => ({
      columns: COLUMNS.map((col) => ({
        accessor: col.accessor as keyof RoseTimeseries | any,
        Header: col.Header,
        key: col.accessor,
        collapse: true,
        Cell: ({ row, value }: any) => {
          switch (col.accessor) {
            case "actions":
              return (
                <ActionsContainer>
                  <DetailsButton
                    modalTitle={row.original.code}
                    metas={row.original.metas}
                  />
                  <Button
                    type="primary"
                    danger={tree === row.original.metas.tree}
                    disabled={!row.original.metas.tree}
                    onClick={() => {
                      if (tree !== row.original.metas.tree) {
                        setTree(row.original.metas.tree);
                      } else if (tree === row.original.metas.tree) {
                        setTree(null);
                      } else {
                        setTree(null);
                      }
                    }}
                  >
                    {tree === row.original.metas.tree
                      ? "Close Tree"
                      : "Show Tree"}
                  </Button>
                  <Tooltip title="Download" placement="left">
                    <DownloadButton
                      roseCode={row.values.code}
                      actorId={row.values.actor}
                    />
                  </Tooltip>
                </ActionsContainer>
              );
            case "units":
              const series = options.series.find(
                (s: any) => s.code === row.original.code,
              );
              return (
                <ChangeUnitsButton
                  axis={
                    options.yAxis[series.yAxis]?.title?.text ||
                    options.yAxis[series.yAxis]?.defaultLabel
                  }
                  chart={options}
                  dataset={row.original.code}
                  onSelectUnit={changeDatasetUnitsRef.current}
                  onSetAll={changeAllDatasetUnits}
                  changeYAxisSide={changeYAxisSide}
                />
              );
            default:
              return (
                <div>
                  <SafeSpan title={value}>{value}</SafeSpan>
                </div>
              );
          }
        },
      })),
      data: options.series,
    }),
    [options.series, tree, changeDatasetUnits, changeAllDatasetUnits],
  );

  return <RoseTableV2 columns={columns} data={data} shown={shown} />;
}

type RoseTableV2Props = {
  columns: Column<RoseTimeseries | any>[];
  data: any[];
  shown: boolean;
};

export function RoseTableV2({ columns, data, shown = true }: RoseTableV2Props) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable<RoseTimeseries | any>(
      {
        columns,
        data,
        initialState: { pageIndex: 0 },
      },
      usePagination,
    );

  return shown ? (
    <Styles>
      <div className="tableWrap">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: columnKey, ...columnProps } =
                      column.getHeaderProps();
                    return (
                      <th key={columnKey} {...columnProps}>
                        {column.render("Header")}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row: Row<RoseTimeseries>) => {
              prepareRow(row);
              const { key: rowKey, ...rowProps } = row.getRowProps();
              return (
                <tr key={rowKey} {...rowProps}>
                  {row.cells.map((cell: any) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <td key={cellKey} {...cellProps}>
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Styles>
  ) : null;
}

const SafeSpan = styled.span`
  overflow-x: hidden;
  display: inherit;
  text-overflow: ellipsis;
`;

const Styles = styled.div`
  /* This is required to make the table full-width */
  display: block;
  max-width: 100%;

  /* This will make the table scrollable when it gets too small */
  .tableWrap {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: auto;
    scrollbar-width: thin;
    border: 1px solid var(${ThemeVarNames.Border});
    border-radius: 10px;
    background-color: var(${ThemeVarNames.PrimaryBg});

    ::-webkit-scrollbar {
      overflow: overlay;
      height: 4px;
      background: unset;
    }

    ::-webkit-scrollbar-thumb {
      display: auto;
      background-clip: padding-box;
      background-color: var(${ThemeVarNames.TertiaryBg});
      border-radius: 20px;
    }
  }

  table {
    /* Make sure the inner table is always as wide as needed */
    width: 100%;
    border-spacing: 0;
    table-layout: fixed;

    thead {
      background-color: var(${ThemeVarNames.SecondaryBg});
    }

    tr {
      &:last-child {
        td {
          border-bottom: calc(0px * 1);
        }
      }
    }

    th,
    td {
      margin: 0.5rem;
      padding: 0.5rem;
      border-bottom: 1px solid var(${ThemeVarNames.Border});

      /* The secret sauce: Each cell should grow equally */
      width: 1%;

      /* But "collapsed" cells should be as small as possible */
      &.collapse {
        width: 0.0000000001%;
      }

      :last-child {
        border-right: calc(0 * 1);
      }
    }

    th {
      color: var(${ThemeVarNames.SecondaryText});
      overflow-x: hidden;
      text-overflow: ellipsis;
    }

    td {
      color: var(${ThemeVarNames.PrimaryText});
    }
  }
`;

const ActionsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;
